import IframeResizer from 'iframe-resizer-react';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import useEvent from 'react-use/lib/useEvent';
import { selectHiddenLeversRtqUrl } from '../../../../../../App/AppSelector';
import { ErrorState } from '../../../../../../CommonComponents/ErrorState/ErrorState';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { FullPageLoading } from '../../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { useRouteParams } from '../../../../../../_hooks/useRouteParams';
import { use3dRTQInputRequestMutation } from '../../api/use3dRTQInputRequestMutation';
import { Results } from '../Results/Results';

enum Status {
  Finish = 'Survey Finished',
}

interface EventData {
  status: Status;
  score: number;
}

interface Props {
  onFinish?(): void;
}

export const IFrameWorkflow = ({ onFinish }: Props) => {
  const [riskScore, setRiskScore] = useState<number | null>(null);

  const { clientId } = useRouteParams<{ clientId: string }>();

  const hiddenLeversRtqUrl = useSelector(selectHiddenLeversRtqUrl);

  const iFrameUrl = `${hiddenLeversRtqUrl}&origin=${window.location.origin}`;

  const { origin: iFrameOrigin } = new URL(iFrameUrl);

  const {
    mutateAsync: save3dRiskScoreMutation,
    isLoading: isSaving3dRiskScoreMutation,
    data: portfolio,
    isError: errorSaving3dRiskScoreMutation,
  } = use3dRTQInputRequestMutation();

  const catchMessage = useCallback(
    async (event: MessageEvent<EventData>) => {
      // Do we trust the sender of the message?
      // It can be different from what we originally expected.
      if (event.origin !== iFrameOrigin) {
        return;
      }

      if (event.data.status === Status.Finish) {
        setRiskScore(event.data.score);

        await save3dRiskScoreMutation({ clientId, detail: { riskScore: event.data.score } });

        if (onFinish) {
          onFinish();
        }
      }
    },
    [clientId, iFrameOrigin, onFinish, save3dRiskScoreMutation],
  );

  useEvent('message', catchMessage);

  if (isSaving3dRiskScoreMutation) {
    return <FullPageLoading text="Loading" />;
  }

  if (errorSaving3dRiskScoreMutation && riskScore) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <ErrorState
          onRetry={async () => {
            await save3dRiskScoreMutation({ clientId, detail: { riskScore } });

            if (onFinish) {
              onFinish();
            }
          }}
        />
      </Box>
    );
  }

  if (portfolio && riskScore) {
    return <Results portfolio={portfolio} onRestart={() => setRiskScore(null)} />;
  }

  return (
    <IframeResizer
      src={iFrameUrl}
      checkOrigin={false}
      frameBorder="0"
      style={{ minHeight: '100%', minWidth: '100%' }}
    />
  );
};
